import { computed, nextTick, ref, type ComputedRef } from 'vue'

export const PROGRESS_UPDATE_INTERVAL = 20
export const IMAGE_DURATION_MS = 10000
export const DEFAULT_SLIDE_DURATION_MS = 30000

export function usePopupModal(currentPopup: ComputedRef<any>) {
  const currentImageIndex = ref(0)
  const imageProgress = ref(0)
  const imageProgressIntervalId = ref<ReturnType<typeof setInterval> | null>(null)
  const videoElement = ref<HTMLVideoElement | null>(null)
  const userPrefersUnmuted = ref(false)
  const isUserSeeking = ref<boolean>(false)
  const wasPausedBeforeSeek = ref<boolean>(false)
  const previousMutedState = ref<boolean>(true)
  const currentVideoDurationMs = ref<number>(0)
  const currentSlideDurationMs = ref<number>(DEFAULT_SLIDE_DURATION_MS)

  const getMediaType = (url: string): 'image' | 'video' => {
    if (!url) return 'image'
    const extension = url.split('.').pop()?.toLowerCase()
    const videoExtensions = ['mp4', 'webm', 'avi', 'mov', 'mkv', 'flv', 'wmv']
    return videoExtensions.includes(extension || '') ? 'video' : 'image'
  }

  const orderedMediaUrls = computed<string[] | null>(() => {
    const list = currentPopup.value?.media_url || []
    if (!list || list.length === 0) return null
    const videos = list.filter((url: string) => getMediaType(url) === 'video')
    const images = list.filter((url: string) => getMediaType(url) === 'image')
    if (videos.length > 0) {
      return [videos[0], ...images]
    }
    return list
  })

  const currentMedia = computed(() => {
    if (!orderedMediaUrls.value || orderedMediaUrls.value.length === 0) return null
    const mediaUrl = orderedMediaUrls.value[currentImageIndex.value] || null
    return mediaUrl
  })

  const currentImageUrl = computed(() => {
    if (!currentMedia.value) return ''
    return currentMedia.value
  })



  const recalculateCurrentSlideDuration = () => {
    const list = orderedMediaUrls.value || []
    const imageCount = list.filter(url => getMediaType(url) === 'image').length
    const hasVideo = list.some(url => getMediaType(url) === 'video')
    let videoMs = 0
    if (hasVideo && currentVideoDurationMs.value > 0) {
      videoMs = currentVideoDurationMs.value
    }
    const total = imageCount * IMAGE_DURATION_MS + videoMs
    if (imageCount > 0 || videoMs > 0) {
      currentSlideDurationMs.value = total
    } else {
      currentSlideDurationMs.value = DEFAULT_SLIDE_DURATION_MS
    }
  }

  const playCurrentVideo = async () => {
    if (currentMedia.value && getMediaType(currentMedia.value) === 'video') {
      try {
        await nextTick()
        let video = videoElement.value
        if (!video && process.client) {
          video = document.querySelector('video') as HTMLVideoElement
        }
        if (video) {
          video.volume = 0.5
          if (userPrefersUnmuted.value) {
            video.muted = false
            if (!isUserSeeking.value) {
              try {
                await video.play()
              } catch (_) {
                video.muted = true
                try { await video.play() } catch (_) {}
              }
            }
          } else {
            video.muted = true
            if (!isUserSeeking.value) {
              try { await video.play() } catch (_) {}
            }
          }
        }
      } catch (_) {}
    }
  }

  const startImageSlideshow = (onLastImageComplete: () => void) => {
    stopImageSlideshow()
    const list = orderedMediaUrls.value || []
    if (!list || list.length === 0) return
    imageProgress.value = 0
    if (list.length === 1) {
      if (getMediaType(list[0]) === 'image') {
        imageProgressIntervalId.value = setInterval(() => {
          imageProgress.value += PROGRESS_UPDATE_INTERVAL
          if (imageProgress.value >= IMAGE_DURATION_MS) {
            imageProgress.value = IMAGE_DURATION_MS
            stopImageSlideshow()
          }
        }, PROGRESS_UPDATE_INTERVAL)
      }
      return
    }
    imageProgressIntervalId.value = setInterval(() => {
      if (currentMedia.value && getMediaType(currentMedia.value) === 'video') {
        imageProgress.value = 0
        return
      }
      imageProgress.value += PROGRESS_UPDATE_INTERVAL
      if (imageProgress.value >= IMAGE_DURATION_MS) {
        nextImage(onLastImageComplete)
      }
    }, PROGRESS_UPDATE_INTERVAL)
  }

  const stopImageSlideshow = () => {
    if (imageProgressIntervalId.value) {
      clearInterval(imageProgressIntervalId.value)
      imageProgressIntervalId.value = null
    }
  }

  const nextImage = (onLastImageComplete: () => void) => {
    const list = orderedMediaUrls.value || []
    if (!list || list.length <= 1) return
    const isLast = currentImageIndex.value + 1 >= list.length
    if (isLast) {
      imageProgress.value = 0
      stopImageSlideshow()
      onLastImageComplete()
      return
    }
    currentImageIndex.value = currentImageIndex.value + 1
    imageProgress.value = 0
  }

  const onVideoLoaded = async (event: Event) => {
    const video = event.target as HTMLVideoElement
    if (video) {
      video.volume = 0.5
      if (userPrefersUnmuted.value) {
        video.muted = false
        if (!isUserSeeking.value) {
          try {
            await video.play()
          } catch (_) {
            video.muted = true
            try { await video.play() } catch (_) {}
          }
        }
      } else {
        video.muted = true
        if (!isUserSeeking.value) {
          try { await video.play() } catch (_) {}
        }
      }
      if (!isNaN(video.duration) && isFinite(video.duration)) {
        currentVideoDurationMs.value = Math.max(0, Math.floor(video.duration * 1000))
        recalculateCurrentSlideDuration()
      }
    }
  }

  const onVideoCanPlay = async (event: Event) => {
    const video = event.target as HTMLVideoElement
    if (video && video.paused && !isUserSeeking.value) {
      try { await video.play() } catch (_) {}
    }
  }

  const onVolumeChange = (event: Event) => {
    const video = event.target as HTMLVideoElement
    if (!video) return
    if (!video.muted && video.volume > 0) {
      userPrefersUnmuted.value = true
      if (previousMutedState.value) {
        video.volume = 0.5
      } else if (video.volume === 0 || video.volume >= 0.99) {
        video.volume = 0.5
      }
      try { sessionStorage.setItem('popupUserPrefersUnmuted', '1') } catch (_) {}
    } else if (video.muted || video.volume === 0) {
      userPrefersUnmuted.value = false
      try { sessionStorage.setItem('popupUserPrefersUnmuted', '0') } catch (_) {}
    }
    previousMutedState.value = video.muted
  }

  const onVideoPlaying = (event: Event) => {
    const video = event.target as HTMLVideoElement
    if (!video) return
    if (userPrefersUnmuted.value) {
      video.muted = false
      video.volume = 0.5
    }
  }

  const onVideoEnded = (onLastImageComplete: () => void) => {
    nextImage(onLastImageComplete)
  }

  const onVideoSeeking = (event: Event) => {
    const video = event.target as HTMLVideoElement
    if (!video) return
    isUserSeeking.value = true
    wasPausedBeforeSeek.value = video.paused
  }

  const onVideoSeeked = async (event: Event) => {
    const video = event.target as HTMLVideoElement
    if (!video) return
    isUserSeeking.value = false
    if (!wasPausedBeforeSeek.value) {
      try { await video.play() } catch (_) {}
    }
  }

  const computeProgressMs = (): number => {
    const list = orderedMediaUrls.value || []
    if (!list || list.length === 0) return 0
    const isVideoNow = currentMedia.value && getMediaType(currentMedia.value) === 'video'
    if (isVideoNow) {
      const video = videoElement.value
      if (video && !isNaN(video.currentTime)) {
        const hasVideo = list.some(url => getMediaType(url) === 'video')
        const imagesBeforeCount = Math.max(0, currentImageIndex.value - (hasVideo ? 1 : 0))
        const base = imagesBeforeCount * IMAGE_DURATION_MS
        return base + Math.max(0, Math.floor(video.currentTime * 1000))
      }
      return 0
    }
    const hasVideo = list.some(url => getMediaType(url) === 'video')
    const imagesBeforeCount = Math.max(0, currentImageIndex.value - (hasVideo ? 1 : 0))
    const base = (hasVideo ? currentVideoDurationMs.value : 0) + imagesBeforeCount * IMAGE_DURATION_MS
    if (list.length === 1 && getMediaType(list[0]) === 'image') {
      return Math.min(IMAGE_DURATION_MS, imageProgress.value)
    }
    return base + imageProgress.value
  }

  const initUserPreference = () => {
    try { userPrefersUnmuted.value = sessionStorage.getItem('popupUserPrefersUnmuted') === '1' } catch (_) {}
  }

  const resetMediaForNewSlide = () => {
    currentImageIndex.value = 0
    imageProgress.value = 0
    currentVideoDurationMs.value = 0
    recalculateCurrentSlideDuration()
  }

  return {
    currentImageIndex,
    videoElement,
    currentSlideDurationMs,
    currentImageUrl,
    recalculateCurrentSlideDuration,
    playCurrentVideo,
    startImageSlideshow,
    stopImageSlideshow,
    nextImage,
    onVideoLoaded,
    onVideoCanPlay,
    onVolumeChange,
    onVideoPlaying,
    onVideoSeeking,
    onVideoSeeked,
    computeProgressMs,
    initUserPreference,
    resetMediaForNewSlide,
    getMediaType,
  }
}


